import axios, { AxiosResponse } from 'axios';
import { CountryOption, StateOption, DonationFormData, ApiResponse } from '../types/donation';

const isProduction = process.env.NODE_ENV === 'production';

// These URLs should be replaced with actual API endpoints
const API_BASE_URL = isProduction 
 ? 'https://ykmainapi.yugalkunj.org/ykrestapi'  // Production URL (placeholder)
  : 'http://localhost:5000/ykrestapi';         // Local development URL


  


// External Yugal Kunj API base (use relative path in dev to go through proxy)
const YK_API_BASE =
  process.env.REACT_APP_YK_API_BASE || '/ykrestapi';
  // Create axios instance with base configuration
  const apiClient = axios.create({
  baseURL: API_BASE_URL,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // API service functions
  export const donationService = {
    // Get available donation types for "This payment is for" select box
    getDonationTypes: async (entity: 'RMS' | 'HYC'): Promise<Array<{ value: string; label: string }>> => {
      try {
        const url = entity === 'HYC'
          ? `${YK_API_BASE}/FloweryChannel/GetDonationType`
          : `${YK_API_BASE}/CommonChannel/GetDonationType`;
        const resp = await axios.get(url, { timeout: 30000 });
        const payload = (resp as any).data;
        const items = (payload?.data ?? payload ?? []) as any[];
        if (!Array.isArray(items) || items.length === 0) {
          if (process.env.NODE_ENV !== 'production') {
            // eslint-disable-next-line no-console
            console.warn('GetDonationType returned empty payload from', url, payload);
          }
          return [];
        }
        // Map to { value, label } trying common shapes
        return items.map((it) => {
          const id = (it?.id ?? it?.Id ?? it?.donationTypeId ?? it?.DonationTypeId ?? it?.Code ?? it?.code ?? it?.name ?? it?.Name);
          const name = (it?.name ?? it?.Name ?? it?.donationType ?? it?.DonationType ?? it?.value ?? String(id));
          return { value: String(name), label: String(name) };
        });
      } catch (error) {
        if (process.env.NODE_ENV !== 'production') {
          // eslint-disable-next-line no-console
          console.warn('Error fetching donation types:', (error as any)?.response?.data || error);
        }
        return [];
      }
    },
  // Get Donations (report) with optional date filters
  getDonations: async (
    entity: 'RMS' | 'HYC',
    fromDate?: string,
    toDate?: string
  ): Promise<any[]> => {
    try {
      const url = entity === 'HYC'
        ? `${YK_API_BASE}/FloweryChannel/GetDonations`
        : `${YK_API_BASE}/CommonChannel/GetDonations`;
      const resp = await axios.get(url, {
        params: {
          fromDate: fromDate || undefined,
          toDate: toDate || undefined,
        },
        timeout: 30000,
      });
      const payload = (resp as any).data;
      const items = (payload?.data ?? payload ?? []) as any[];
      if (!Array.isArray(items)) return [];
      return items;
    } catch (error: any) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn('GetDonations failed:', error?.response?.data || error);
      }
      return [];
    }
  },
  // Verify Google reCAPTCHA token via YK API. Falls back to same-origin PHP endpoint in dev if needed.
  verifyRecaptcha: async (token: string): Promise<{ success: boolean; [k: string]: any }> => {
    // Primary: YK API endpoint
    const ykUrl = `${YK_API_BASE}/PaymentChannel/verify-recaptcha`;
    try {
      // Backend signature: VerifyCaptcha([FromBody] string captchaToken)
      // Send token as a JSON string literal so ASP.NET binds and ModSecurity is less likely to block
      const resp = await axios.post(
        ykUrl,
        JSON.stringify(token),
        {
          headers: {
            'Content-Type': 'application/json',
            'X-Requested-With': 'XMLHttpRequest',
          },
          timeout: 15000,
        }
      );
      return (resp as any).data || { success: false };
    } catch (primaryErr: any) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('Primary YK verify-recaptcha failed, falling back to local PHP endpoint...', primaryErr?.code || primaryErr?.message);
      }
      // Fallback: same-origin PHP endpoint for local testing
      try {
        const localUrl = '/recaptcha-verify.php';
        const resp2 = await axios.post(localUrl, { token }, {
          headers: { 'Content-Type': 'application/json' },
          timeout: 15000,
        });
        return (resp2 as any).data || { success: false };
      } catch (fallbackErr: any) {
        console.error('reCAPTCHA verify failed (both YK and local):', fallbackErr?.response?.data || fallbackErr);
        return { success: false } as any;
      }
    }
  },
  // Get countries list from database
  getCountries: async (): Promise<CountryOption[]> => {
    try {
      // Use YK CommonChannel endpoint with proxy in dev and fallback to absolute host on timeout
      const primaryUrl = `${YK_API_BASE}/CommonChannel/GetCountry`;
      let payload: any;
      try {
        const resp = await axios.get(primaryUrl, { timeout: 30000 });
        payload = (resp as any).data;
      } catch (err: any) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('Primary GetCountry call failed, retrying absolute URL...', err?.code || err?.message);
        }
        // Fallback uses the same environment-specific base
        const absUrl = `${YK_API_BASE}/CommonChannel/GetCountry`;
        const resp2 = await axios.get(absUrl, { timeout: 30000 });
        payload = (resp2 as any).data;
      }
      const items = (payload?.data ?? payload ?? []) as Array<{
        id?: number | string;
        name?: string;
        CountryId?: number | string;
        CountryID?: number | string;
        countryId?: number | string;
        country_Id?: number | string; // new shape
        CountryName?: string;
        countryName?: string;
        country_Name?: string; // new shape
        CountryCode?: string;
        countryCode?: string;
        shortCode?: string | null; // new shape
      }>;
      if (!Array.isArray(items) || items.length === 0) {
        console.warn('GetCountry returned empty payload from', `${YK_API_BASE}/CommonChannel/GetCountry`, payload);
      }
      // Map to dropdown-friendly shape
      return items.map((c) => {
        const rawId = (c.id ?? c.CountryId ?? (c as any).CountryID ?? (c as any).countryId ?? (c as any).country_Id) as number | string | undefined;
        const idNum = typeof rawId === 'string' ? parseInt(rawId, 10) : Number(rawId);
        const id = Number.isFinite(idNum) ? idNum : 0;
        const name = (c.name ?? c.CountryName ?? (c as any).countryName ?? (c as any).country_Name) as string;
        const code = (c as any).shortCode ?? (c as any).CountryCode ?? (c as any).countryCode;
        return {
          id,
          name,
          // always use numeric id as string so downstream state API receives a valid number
          value: String(id),
          label: name,
          code,
        } as CountryOption;
      });
    } catch (error) {
      console.error('Error fetching countries:', error);
      return [];
    }
  },

  // Get states list for a specific country using country code (kept for compatibility; uses GET)
  getStatesByCountryCode: async (countryCode: string): Promise<StateOption[]> => {
    try {
      const primaryUrl = `${YK_API_BASE}/CommonChannel/GetState`;
      let payload: any;
      try {
        const resp = await axios.get(primaryUrl, {
          params: { countryCode },
          timeout: 30000,
        });
        payload = (resp as any).data;
      } catch (err: any) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('Primary GetState(by code) failed, retrying absolute URL...', err?.code || err?.message);
        }
        // Fallback uses the same environment-specific base
        const absUrl = `${YK_API_BASE}/CommonChannel/GetState`;
        const resp2 = await axios.get(absUrl, {
          params: { countryCode },
          timeout: 30000,
        });
        payload = (resp2 as any).data;
      }
      const items = (payload?.data ?? payload ?? []) as Array<{
        id?: number | string;
        name?: string;
        StateId?: number | string;
        StateID?: number | string;
        userStateId?: number | string; // new shape
        StateName?: string;
        stateName?: string; // new shape
        CountryId?: number | string;
        country_Id?: number | string; // new shape
      }>; 
      if (!Array.isArray(items) || items.length === 0) {
        console.warn('GetState (by code) returned empty payload from', `${YK_API_BASE}/CommonChannel/GetState`, 'for countryCode', countryCode, payload);
      }
      return items.map((s) => {
        const rawSid = (s.id ?? s.StateId ?? (s as any).StateID ?? (s as any).userStateId) as number | string | undefined;
        const sid = typeof rawSid === 'string' ? parseInt(rawSid, 10) : Number(rawSid);
        const sname = (s.name ?? s.StateName ?? (s as any).stateName) as string;
        return {
          id: sid,
          name: sname,
          countryId: ((s as any).CountryId ?? (s as any).country_Id) as number,
          value: String(sid),
          label: sname,
        } as StateOption;
      });
    } catch (error) {
      console.error('Error fetching states by country code:', error);
      return [];
    }
  },

  // Get states list for a specific country from database (POST body as per API requirement)
  getStatesByCountry: async (countryId: number): Promise<StateOption[]> => {
    try {
      // Use YK CommonChannel endpoint with proxy in dev and fallback to absolute host on timeout
      const primaryUrl = `${YK_API_BASE}/CommonChannel/GetState`;
      const requestBody = { userStateId: 0, stateName: '', country_Id: countryId };
      let payload: any;
      try {
        const resp = await axios.post(primaryUrl, requestBody, {
          headers: { 'Content-Type': 'application/json' },
          timeout: 30000,
        });
        payload = (resp as any).data;
      } catch (err: any) {
        if (process.env.NODE_ENV !== 'production') {
          console.warn('Primary GetState POST call failed, retrying absolute URL...', err?.code || err?.message);
        }
        // Fallback uses the same environment-specific base
        const absUrl = `${YK_API_BASE}/CommonChannel/GetState`;
        const resp2 = await axios.post(absUrl, requestBody, {
          headers: { 'Content-Type': 'application/json' },
          timeout: 30000,
        });
        payload = (resp2 as any).data;
      }
      const items = (payload?.data ?? payload ?? []) as Array<{ id?: number; name?: string; StateId?: number; StateName?: string; CountryId?: number }>;
      if (!Array.isArray(items) || items.length === 0) {
        console.warn('GetState returned empty payload from', `${YK_API_BASE}/CommonChannel/GetState`, 'for countryId', countryId, payload);
      }
      // Map to dropdown-friendly shape and attach countryId context if missing
      return items.map((s) => {
        const rawSid = (s.id ?? s.StateId ?? (s as any).StateID ?? (s as any).userStateId) as number | string | undefined;
        const sid = typeof rawSid === 'string' ? parseInt(rawSid, 10) : Number(rawSid);
        const sname = (s.name ?? s.StateName ?? (s as any).stateName) as string;
        return {
          id: sid,
          name: sname,
          countryId: ((s as any).CountryId ?? (s as any).country_Id ?? countryId) as number,
          value: String(sid),
          label: sname,
        } as StateOption;
      });
    } catch (error) {
      console.error('Error fetching states:', error);
      return [];
    }
  },

  // Submit donation payment
  submitDonation: async (donationData: DonationFormData): Promise<ApiResponse> => {
    try {
      const response: AxiosResponse<ApiResponse> = await apiClient.post('/donation/payment', donationData);
      return response.data;
    } catch (error) {
      console.error('Error submitting donation:', error);
      return { success: false, message: 'Failed to process donation' };
    }
  },
  
  // Send email notification
  sendEmailNotification: async (donationData: DonationFormData): Promise<ApiResponse> => {
    try {
      const response: AxiosResponse<ApiResponse> = await apiClient.post('/donation/email-notification', donationData);
      return response.data;
    } catch (error) {
      console.error('Error sending email notification:', error);
      return { success: false, message: 'Failed to send email notification' };
    }
  },

   // One-Time Payment via AuthNet
   authNetOneTimePaymentHYC: async (payload: any): Promise<any> => {
    try {
      const url = `${YK_API_BASE}/FloweryChannel/AuthNetOneTimePayment`;
      const response = await axios.post(url, payload, { headers: { 'Content-Type': 'application/json' } });
      return response.data;
    } catch (error: any) {
      console.error('AuthNet one-time payment error:', error?.response?.data || error);
      throw error;
    }
  },

  // Recurring Payment via AuthNet
  authNetRecurringPaymentHYC: async (payload: any, studentId: string = '0'): Promise<any> => {
    try {
      const url = `${YK_API_BASE}/FloweryChannel/AuthNetRecurringPayment?studentId=${encodeURIComponent(studentId ?? '0')}`;
      const response = await axios.post(url, payload, { headers: { 'Content-Type': 'application/json' } });
      return response.data;
    } catch (error: any) {
      console.error('AuthNet recurring payment error:', error?.response?.data || error);
      throw error;
    }
  },

  // One-Time Payment via AuthNet
  authNetOneTimePayment: async (payload: any): Promise<any> => {
    try {
      const url = `${YK_API_BASE}/PaymentChannel/AuthNetOneTimePayment`;
      const response = await axios.post(url, payload, { headers: { 'Content-Type': 'application/json' } });
      return response.data;
    } catch (error: any) {
      console.error('AuthNet one-time payment error:', error?.response?.data || error);
      throw error;
    }
  },

  // Recurring Payment via AuthNet
  authNetRecurringPayment: async (payload: any, studentId: string = '0'): Promise<any> => {
    try {
      const url = `${YK_API_BASE}/PaymentChannel/AuthNetRecurringPayment?studentId=${encodeURIComponent(studentId ?? '0')}`;
      const response = await axios.post(url, payload, { headers: { 'Content-Type': 'application/json' } });
      return response.data;
    } catch (error: any) {
      console.error('AuthNet recurring payment error:', error?.response?.data || error);
      throw error;
    }
  },

  // Send Email via MessageChannel (defaults aligned with C# sample)
  sendEmailMessage: async (
    data: {
      Monaris_Registration_Id?: number;
      Email: string;
      ContactNo: string;
      UserName: string;
      DonationDate?: string | Date;
      Amount: string;
      Address: string;
      StartDate?: string;
      EndDate?: string;
      // Additional fields for grocery donation template
      PaymentType?: string;
      ItemData?: string;
      Instructions?: string;
    },
    options?: { appName?: string; templateName?: string; orgId?: string | number }
  ): Promise<any> => {
    const { Email, ContactNo, UserName, DonationDate, Amount, Address, StartDate, EndDate, PaymentType, ItemData, Instructions } = data;
    const appName = options?.appName ?? 'Donation';
    const templateName = options?.templateName ?? 'RMSDonation';
    const orgId = options?.orgId ?? 3;

    // Build payload similar to FireRequestEmail in C#
    // Use templateName as location for proper template matching
    const payload = {
      emailFrom: 'string',
      emailBody: 'string',
      emailSubject: 'string',
      attachment: 'Y',
      userPhoneNumber: ContactNo,
      emailTo: Email,
      location: templateName,
      appContent: {
        userName: String(UserName ?? ''),
        name: String(UserName ?? ''),
        programName: 'string',
        appSchedule: 'string',
        attendee: 'string',
        date: (DonationDate ? new Date(DonationDate) : new Date()).toString(),
        orderType: 'string',
        orderNo: 'string',
        paymentMode: 'string',
        amount: Amount,
        email: Email,
        phone: ContactNo || 'string',
        pickupPerson: '',
        pickupPersonContact: `+${ContactNo}`,
        instructions: Instructions || 'string',
        itemData: ItemData || 'string',
        pickUpDate: 'string',
        pickUpTime: 'string',
        paymentType: PaymentType || 'string',
        billingAddress: 'string',
        address: String(Address ?? ''),
        pickupOrReceiver: 'string',
        orderReady: Amount,
        orderReadyLastLine: 'string',
        headingDate: StartDate || '',
        headingTime: EndDate || '',
        startDate: StartDate || '',
        endDate: EndDate || '',
      },
    };

    try {
      const url = `${YK_API_BASE}/MessageChannel/SendEmailMessage?appName=${encodeURIComponent(
        appName
      )}&templateName=${encodeURIComponent(templateName)}&orgId=${encodeURIComponent(String(orgId))}`;
      const response = await axios.post(url, payload, { headers: { 'Content-Type': 'application/json' } });
      return response.data;
    } catch (error: any) {
      if (process.env.NODE_ENV !== 'production') {
        // eslint-disable-next-line no-console
        console.warn('SendEmailMessage failed (non-blocking):', error?.response?.data || error?.message || error);
      }
      // Do not throw — callers should continue user flow without surfacing console errors
      return { success: false, message: 'Email send failed' };
    }
  },

  // Add Donation record via CommonChannel
  addDonation: async (
    data: {
      app: string;
      entity: string;
      firstName: string;
      lastName: string;
      email: string;
      phoneNo: string;
      address: string;
      country: string;
      state: string;
      city: string;
      postalCode: string;
      purposeOfDonation: string;
      paymentFor: string;
      donationAmount: number;
      donationType: string;
      paymentType: string;
    }
  ): Promise<any> => {
    try {
      const url = `${YK_API_BASE}/CommonChannel/AddDonation`;
      const response = await axios.post(url, data, {
        headers: { 'Content-Type': 'application/json' },
      });
      return (response as any).data;
    } catch (error: any) {
      console.error('AddDonation error:', error?.response?.data || error);
      throw error;
    }
  },

  addHYCDonation: async (
    data: {
      app: string;
      entity: string;
      firstName: string;
      lastName: string;
      email: string;
      phoneNo: string;
      address: string;
      country: string;
      state: string;
      city: string;
      postalCode: string;
      purposeOfDonation: string;
      paymentFor: string;
      donationAmount: number;
      donationType: string;
      paymentType: string;
    }
  ): Promise<any> => {
    try {
      const url = `${YK_API_BASE}/FloweryChannel/AddDonation`;
      const response = await axios.post(url, data, {
        headers: { 'Content-Type': 'application/json' },
      });
      return (response as any).data;
    } catch (error: any) {
      console.error('AddDonation error:', error?.response?.data || error);
      throw error;
    }
  },

  // Add Grocery Donation
  addGroceryDonation: async (data: any): Promise<any> => {
    try {
      const url = `${YK_API_BASE}/CommonChannel/AddGroceryDonation`;
      const response = await axios.post(url, data, {
        headers: { 'Content-Type': 'application/json' },
      });
      return (response as any).data;
    } catch (error: any) {
      console.error('AddGroceryDonation error:', error?.response?.data || error);
      throw error;
    }
  },

  // Send Grocery Donation Email
  sendGroceryDonationEmail: async (data: any): Promise<any> => {
    try {
      const url = `${YK_API_BASE}/CommonChannel/SendGroceryDonationEmail`;
      const response = await axios.post(url, data, {
        headers: { 'Content-Type': 'application/json' },
      });
      return (response as any).data;
    } catch (error: any) {
      if (process.env.NODE_ENV !== 'production') {
        console.warn('SendGroceryDonationEmail failed (non-blocking):', error?.response?.data || error?.message || error);
      }
      return { success: false, message: 'Email send failed' };
    }
  },

  // Get Grocery Items from Database
  getGroceryItems: async (retryCount = 0): Promise<any[]> => {
    try {
      const url = `${YK_API_BASE}/CommonChannel/GetGroceryItems`;
      const response = await axios.get(url, {
        headers: { 'Content-Type': 'application/json' },
      });
      return (response as any).data || [];
    } catch (error: any) {
      console.error('GetGroceryItems error:', error?.response?.data || error);
      // Retry up to 2 times with delay for transient failures
      if (retryCount < 2) {
        await new Promise(resolve => setTimeout(resolve, 500 * (retryCount + 1)));
        return donationService.getGroceryItems(retryCount + 1);
      }
      // Return empty array on error, component will handle fallback
      return [];
    }
  },

  // Add Grocery Item (Admin)
  addGroceryItem: async (data: {
    itemId: string;
    itemName: string;
    estimatedPrice: number;
    icon: string;
    isActive: boolean;
    displayOrder: number;
  }): Promise<any> => {
    try {
      const url = `${YK_API_BASE}/CommonChannel/AddGroceryItem`;
      const payload = {
        Id: data.itemId || null,
        Name: data.itemName,
        EstimatedPrice: data.estimatedPrice,
        Icon: data.icon,
        IsActive: data.isActive,
        DisplayOrder: data.displayOrder,
      };
      const response = await axios.post(url, payload, {
        headers: { 'Content-Type': 'application/json' },
      });
      return (response as any).data;
    } catch (error: any) {
      console.error('AddGroceryItem error:', error?.response?.data || error);
      throw error;
    }
  },

  // Update Grocery Item (Admin)
  updateGroceryItem: async (data: {
    itemId: string;
    itemName: string;
    estimatedPrice: number;
    icon: string;
    isActive: boolean;
    displayOrder: number;
  }): Promise<any> => {
    try {
      const url = `${YK_API_BASE}/CommonChannel/UpdateGroceryItem`;
      const payload = {
        Id: data.itemId,
        Name: data.itemName,
        EstimatedPrice: data.estimatedPrice,
        Icon: data.icon,
        IsActive: data.isActive,
        DisplayOrder: data.displayOrder,
      };
      const response = await axios.post(url, payload, {
        headers: { 'Content-Type': 'application/json' },
      });
      return (response as any).data;
    } catch (error: any) {
      console.error('UpdateGroceryItem error:', error?.response?.data || error);
      throw error;
    }
  },

  // Delete Grocery Item (Admin)
  deleteGroceryItem: async (itemId: string): Promise<any> => {
    try {
      const url = `${YK_API_BASE}/CommonChannel/DeleteGroceryItem/${itemId}`;
      const response = await axios.delete(url, {
        headers: { 'Content-Type': 'application/json' },
      });
      return (response as any).data;
    } catch (error: any) {
      console.error('DeleteGroceryItem error:', error?.response?.data || error);
      throw error;
    }
  },
};

export default donationService;
